import axiosClient from './axiosClient';

let mockHistory = [
  {
    historyID: 1,
    ticketID: 1,
    fromStatusID: 1, // Open
    toStatusID: 2, // In Progress
    changedBy: 1, // Admin User (e.g. John Doe)
    changedDttm: '2026-02-05T10:00:00Z',
  },
  {
    historyID: 2,
    ticketID: 1,
    fromStatusID: 2, // In Progress
    toStatusID: 3, // Resolved
    changedBy: 2, // PM User (e.g. Jane Smith)
    changedDttm: '2026-02-06T15:30:00Z',
  },
  {
    historyID: 3,
    ticketID: 2,
    fromStatusID: 1, // Open
    toStatusID: 2, // In Progress
    changedBy: 2, // PM User
    changedDttm: '2026-02-07T09:15:00Z',
  },
];

const ticketStatusHistoryService = {
  getAll: (params = {}) => {
    return axiosClient
      .get('/TicketStatusHistory', { params })
      .catch(() => axiosClient.get('/TicketHistory', { params }))
      .then((res) => {
        const raw = res?.data?.data || res?.data;
        if (Array.isArray(raw) && raw.length > 0) {
          const mapped = raw.map((item, idx) => ({
            historyID: item.historyId || item.historyID || item.id || (idx + 1),
            ticketID: item.ticketId || item.ticketID,
            fromStatusID: item.fromStatusId || item.fromStatusID,
            toStatusID: item.toStatusId || item.toStatusID,
            fromStatusName: item.fromStatusName || item.fromStatus,
            toStatusName: item.toStatusName || item.toStatus,
            changedBy: item.changedBy || item.userId || item.userID,
            changedByName: item.changedByName || item.userName,
            changedDttm: item.changedDttm || item.createdDttm || item.createdDate || item.createDate,
          }));
          return { data: mapped };
        }
        throw new Error('Fallback to mock');
      })
      .catch(() => {
        let list = [...mockHistory];
        if (params.ticketID) {
          list = list.filter((h) => Number(h.ticketID || h.ticketId) === Number(params.ticketID));
        }
        if (params.search) {
          const q = String(params.search).toLowerCase();
          list = list.filter(
            (h) =>
              String(h.ticketID || h.ticketId).includes(q) ||
              String(h.historyID || h.historyId).includes(q)
          );
        }
        return { data: list };
      });
  },

  getById: (historyID) => {
    return axiosClient
      .get(`/TicketStatusHistory/${historyID}`)
      .catch(() => axiosClient.get(`/TicketHistory/${historyID}`))
      .then((res) => {
        const raw = res?.data?.data || res?.data;
        if (raw) {
          return {
            data: {
              historyID: raw.historyId || raw.historyID || raw.id,
              ticketID: raw.ticketId || raw.ticketID,
              fromStatusID: raw.fromStatusId || raw.fromStatusID,
              toStatusID: raw.toStatusId || raw.toStatusID,
              fromStatusName: raw.fromStatusName || raw.fromStatus,
              toStatusName: raw.toStatusName || raw.toStatus,
              changedBy: raw.changedBy || raw.userId || raw.userID,
              changedByName: raw.changedByName || raw.userName,
              changedDttm: raw.changedDttm || raw.createdDttm || raw.createdDate,
            },
          };
        }
        throw new Error('Fallback');
      })
      .catch(() => {
        const item = mockHistory.find((h) => h.historyID === Number(historyID));
        return { data: item || null };
      });
  },

  create: (payload) => {
    const newID = mockHistory.length > 0 ? Math.max(...mockHistory.map((h) => h.historyID)) + 1 : 1;
    const newRecord = {
      historyID: newID,
      ticketID: payload.ticketID ? Number(payload.ticketID) : null,
      fromStatusID: payload.fromStatusID ? Number(payload.fromStatusID) : null,
      toStatusID: payload.toStatusID ? Number(payload.toStatusID) : null,
      changedBy: payload.changedBy ? Number(payload.changedBy) : 1,
      changedDttm: new Date().toISOString(),
    };
    mockHistory.unshift(newRecord);

    const body = {
      historyId: 0,
      ticketId: newRecord.ticketID,
      fromStatusId: newRecord.fromStatusID,
      toStatusId: newRecord.toStatusID,
      changedBy: newRecord.changedBy,
      changedDttm: newRecord.changedDttm,
    };

    return axiosClient
      .post('/TicketStatusHistory', body)
      .catch(() => axiosClient.post('/TicketHistory', body))
      .then((res) => {
        const raw = res?.data?.data || res?.data;
        return { data: raw || newRecord };
      })
      .catch(() => {
        return { data: newRecord };
      });
  },

  update: (historyID, payload) => {
    const index = mockHistory.findIndex((h) => h.historyID === Number(historyID));
    if (index !== -1) {
      mockHistory[index] = {
        ...mockHistory[index],
        ticketID: payload.ticketID ? Number(payload.ticketID) : mockHistory[index].ticketID,
        fromStatusID: payload.fromStatusID ? Number(payload.fromStatusID) : mockHistory[index].fromStatusID,
        toStatusID: payload.toStatusID ? Number(payload.toStatusID) : mockHistory[index].toStatusID,
        changedBy: payload.changedBy ? Number(payload.changedBy) : mockHistory[index].changedBy,
      };
    }

    const body = {
      historyId: Number(historyID),
      ticketId: payload.ticketID ? Number(payload.ticketID) : undefined,
      fromStatusId: payload.fromStatusID ? Number(payload.fromStatusID) : undefined,
      toStatusId: payload.toStatusID ? Number(payload.toStatusID) : undefined,
      changedBy: payload.changedBy ? Number(payload.changedBy) : undefined,
    };

    return axiosClient
      .put('/TicketStatusHistory', body)
      .catch(() => axiosClient.put(`/TicketStatusHistory/${historyID}`, body))
      .then((res) => {
        const raw = res?.data?.data || res?.data;
        return { data: raw || (index !== -1 ? mockHistory[index] : payload) };
      })
      .catch(() => {
        if (index !== -1) {
          return { data: mockHistory[index] };
        }
        return Promise.reject({ response: { data: { message: 'History record not found.' } } });
      });
  },

  remove: (historyID) => {
    mockHistory = mockHistory.filter((h) => h.historyID !== Number(historyID));
    return axiosClient
      .delete(`/TicketStatusHistory/${historyID}`)
      .catch(() => axiosClient.delete(`/TicketHistory/${historyID}`))
      .then(() => ({ data: { success: true } }))
      .catch(() => ({ data: { success: true } }));
  },
};

export default ticketStatusHistoryService;
